"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
interface StepItem {
  number: string;
  title: string;
  description: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  details: string[];
}
const steps: StepItem[] = [{
  number: "01",
  title: "On-site consultation",
  description: "We meet at your home to evaluate your property's topography, discuss your goals, and assess structural feasibility.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_consultation_backyard_plan.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_consultation_backyard_plan.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_consultation_backyard_plan.png-wvc-sizes",
    alt: "Contractor reviewing outdoor patio blueprints with homeowners in backyard"
  },
  details: ["Property elevation and boundary evaluation", "Functionality and aesthetic goals discussion", "Initial feasibility and preliminary budget alignment"]
}, {
  number: "02",
  title: "Design and spatial planning",
  description: "Our design team translates your requirements into architectural layouts, 3D renderings, and precise material selections.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_kitchen_deck_design_render.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_kitchen_deck_design_render.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_kitchen_deck_design_render.png-wvc-sizes",
    alt: "Architectural 3D design plans for custom outdoor kitchen and covered deck"
  },
  details: ["Photorealistic 3D visualization and elevations", "Material, hardscape, and appliance specifications", "Transparent itemized scope of work"]
}, {
  number: "03",
  title: "Permitting and documentation",
  description: "We handle all municipal zoning filings, county building permits, and HOA documentation before breaking ground.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_building_permits_inspection.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_building_permits_inspection.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_building_permits_inspection.png-wvc-sizes",
    alt: "Approved municipal architectural permits and zoning documentation on a desk"
  },
  details: ["Municipal building permit submission and follow-through", "HOA and neighborhood architectural review compliance", "Pre-construction utility locating and site readiness"]
}, {
  number: "04",
  title: "Framing and construction",
  description: "Our certified trades build the hardscaping, covered rooflines, masonry, electrical, and utilities to exact code standards.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_deck_masonry_construction.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_deck_masonry_construction.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_deck_masonry_construction.webp-wvc-sizes",
    alt: "Skilled carpenters constructing timber frame beams for covered outdoor porch"
  },
  details: ["Structural timber framing and stone masonry", "Underground plumbing, electrical, and gas lines", "Dedicated project manager on-site daily"]
}, {
  number: "05",
  title: "Final walkthrough",
  description: "We conduct an exhaustive room-by-room review, demonstrate systems, and ensure every detail meets our benchmark.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_patio_fire_pit_completed.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_patio_fire_pit_completed.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_patio_fire_pit_completed.png-wvc-sizes",
    alt: "Finished stone patio with outdoor fireplace and comfortable lounge seating"
  },
  details: ["Comprehensive punch list completion", "Equipment, lighting, and appliance demonstration", "Warranty documentation and care guide delivery"]
}];
export default function OutdoorProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="fivestepoutdoorprocess" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className={`max-w-3xl mb-14 lg:mb-20 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Badge variant="secondary" className="mb-4 text-xs font-mono uppercase tracking-wider">
            Clear Project Roadmap
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground text-balance">
            Our five-step outdoor living process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From your first site assessment through to the final walkthrough, our organized sequence ensures clear communication, orderly timelines, and exacting craft.
          </p>
        </div>

        <div className="space-y-12 lg:space-y-16">
          {steps.map((step, index) => {
          const isEven = index % 2 === 1;
          return <div key={step.number} data-index={index} className={`transition-all duration-700 ease-out delay-[${index * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <Card className="overflow-hidden border border-border/70 bg-card shadow-sm hover:shadow-md transition-shadow">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-center">
                    <div className={`lg:col-span-5 relative h-64 sm:h-80 lg:h-full min-h-[280px] lg:min-h-[360px] ${isEven ? "lg:order-2" : "lg:order-1"}`}>
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      <div className="absolute top-4 left-4 bg-secondary/90 text-secondary-foreground text-xs font-mono px-3 py-1.5 rounded-md backdrop-blur-xs font-semibold">
                        Step {step.number}
                      </div>
                    </div>

                    <CardContent className={`lg:col-span-7 p-6 sm:p-8 lg:p-12 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-mono text-xs text-primary font-medium tracking-widest uppercase">
                          Phase {step.number} of 05
                        </span>
                      </div>

                      <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground tracking-tight mb-4">
                        {step.title}
                      </h3>

                      <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6">
                        {step.description}
                      </p>

                      <div className="pt-4 border-t border-border">
                        <ul className="space-y-2.5">
                          {step.details.map((detail, dIndex) => <li key={dIndex} className="flex items-start gap-2.5 text-sm text-foreground/90">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span>{detail}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 lg:mt-20 pt-10 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-card p-8 rounded-xl border">
          <div>
            <h4 className="font-serif text-xl sm:text-2xl font-medium text-foreground">
              Ready to plan your outdoor renovation?
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Schedule your introductory on-site consultation with our team.
            </p>
          </div>
          <Button asChild size="lg" className="w-full sm:w-auto">
            <Link to="/ContactRequestAnEstimatePage">
              Request an estimate
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}